import * as RadixTooltip from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import {
  ComponentPropsWithoutRef,
  CSSProperties,
  forwardRef,
  ReactNode,
} from 'react';
import { mergeProps, RADIX_COLLISION_PADDING } from 'src/core/utils';

import * as styles from './Tooltip.css';

export const Provider = RadixTooltip.Provider;

export const Root = RadixTooltip.Root;

export const Trigger = RadixTooltip.Trigger;

export const Content = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixTooltip.Content> & {
    contentVariant?: 'theme' | 'light' | 'dark' | 'darkGray';
    showArrow?: boolean;
    postfixDisplay?: ReactNode;
  }
>(function Content(
  { children, contentVariant, showArrow = true, postfixDisplay, ...props },
  ref
) {
  const tooltipChildren = (
    <>
      {children}
      {showArrow && (
        <RadixTooltip.Arrow
          className={clsx(styles.tooltipArrow, {
            [styles.lightContentArrow]: contentVariant === 'light',
            [styles.darkContentArrow]: contentVariant === 'dark',
            [styles.darkGrayContentArrow]: contentVariant === 'darkGray',
          })}
        />
      )}
    </>
  );

  return (
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        ref={ref}
        sideOffset={4}
        collisionPadding={RADIX_COLLISION_PADDING}
        {...mergeProps(
          {
            className: clsx(
              postfixDisplay
                ? styles.tooltipContentWithPostfix
                : styles.tooltipContent,
              {
                [styles.lightContentWrapper]:
                  contentVariant === 'light' && !postfixDisplay,
                [styles.darkContentWrapper]:
                  contentVariant === 'dark' && !postfixDisplay,
                [styles.darkGrayContentWrapper]:
                  contentVariant === 'darkGray' && !postfixDisplay,
              }
            ),
          },
          props
        )}
      >
        {postfixDisplay ? (
          <div className={styles.tooltipContentWrapper}>
            <div className={styles.tooltipContentSectionLeft}>
              {tooltipChildren}
            </div>
            <div className={styles.tooltipDivider} />
            <div className={styles.tooltipContentSectionRight}>
              {postfixDisplay}
            </div>
          </div>
        ) : (
          tooltipChildren
        )}
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
});

export const Tooltip = ({
  triggerContent,
  actionContent,
  children,
  delayDuration = 300,
  align = 'start',
  contentVariant,
  contentStyle,
  open,
  side,
  showArrow = true,
  postfixDisplay,
  ...rest
}: RadixTooltip.TooltipTriggerProps & {
  triggerContent: ReactNode;
  actionContent?: ReactNode;
  delayDuration?: RadixTooltip.TooltipProps['delayDuration'];
  onOpenChange?: RadixTooltip.TooltipProps['onOpenChange'];
  open?: RadixTooltip.TooltipProps['open'];
  align?: RadixTooltip.TooltipContentProps['align'];
  contentVariant?: 'theme' | 'light' | 'dark' | 'darkGray';
  contentStyle?: CSSProperties;
  side?: RadixTooltip.TooltipContentProps['side'];
  showArrow?: boolean;
  postfixDisplay?: ReactNode;
}) => {
  return (
    <Provider>
      <Root delayDuration={delayDuration} open={open}>
        <Trigger asChild {...rest}>
          {triggerContent}
        </Trigger>
        <Content
          align={align}
          contentVariant={contentVariant}
          style={contentStyle}
          side={side}
          showArrow={showArrow}
          postfixDisplay={postfixDisplay}
        >
          {children}
        </Content>
      </Root>
    </Provider>
  );
};
